.list {
  list-style: none;
  margin: 0;
  padding: 8px 0;
}

.dense {
  padding: 4px 0;
}

.item {
  display: flex;
  align-items: center;
  position: relative;
  padding: 8px 16px;
  text-decoration: none;
  width: 100%;
  box-sizing: border-box;
  border-radius: var(--tuna-shape-radius-md);
  margin-top: 4px;
  margin-bottom: 4px;
}

.itemDense {
  padding: 4px 16px;
}

.itemButton {
  cursor: pointer;
  background: none;
  border: none;
  font: inherit;
  color: inherit;
  text-align: start;
  -webkit-tap-highlight-color: transparent;
}

/* Touch: 44px rows (GH #2406, WCAG 2.5.5); font unchanged, desktop stays dense */
@media (pointer: coarse) {
  .itemButton {
    min-height: 44px;
  }
}

.itemButton:hover,
.itemButton[aria-selected='true'],
.item[tabindex]:hover {
  background-color: var(--tuna-palette-action-hover);
}

.itemSelected {
  background-color: var(--tuna-palette-action-selected);
}

.itemIcon {
  display: inline-flex;
  flex-shrink: 0;
  color: var(--tuna-palette-action-active);
  padding-right: 8px;
}

.itemAvatar {
  display: inline-flex;
  min-width: 56px;
  flex-shrink: 0;
}

.itemText {
  flex: 1 1 auto;
  min-width: 0;
}

.itemTextPrimary {
  display: block;
  font-size: var(--tuna-typography-body1-fontSize);
  line-height: var(--tuna-typography-body1-lineHeight);
  color: var(--tuna-palette-text-primary);
}

.itemTextSecondary {
  display: block;
  font-size: var(--tuna-typography-body2-fontSize);
  line-height: var(--tuna-typography-body2-lineHeight);
  color: var(--tuna-palette-text-secondary);
}

.itemSecondaryAction {
  position: absolute;
  right: 16px;
  top: 50%;
  transform: translateY(-50%);
}

.menuItem {
  position: relative;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 16px;
  font-family: var(--tuna-typography-fontFamily);
  font-size: var(--tuna-typography-body2-fontSize);
  line-height: var(--tuna-typography-body2-lineHeight);
  cursor: pointer;
  white-space: nowrap;
  background: none;
  border: none;
  width: 100%;
  text-align: start;
  color: inherit;
  text-decoration: none;
  -webkit-tap-highlight-color: transparent;
  user-select: none;
}

.menuItem svg {
  width: 20px;
  height: 20px;
}

.menuItem .itemTextPrimary {
  font-size: inherit;
  line-height: inherit;
}

.menuItem[data-highlighted],
.menuItem:hover,
.menuItem:focus-visible {
  background-color: var(--tuna-palette-action-hover);
  outline: none;
}

.menuItemSelected {
  background-color: var(--tuna-palette-action-selected);
}

.menuItem[data-disabled],
.menuItem:disabled,
.menuItem[aria-disabled='true'] {
  color: var(--tuna-palette-action-disabled);
  pointer-events: none;
}

/* The name sets its own colour, so a disabled item's never reached it: a disabled "Move…" read as enabled. Dim the
 * name only; the reason (secondary) stays readable (GH #2529, #2450). Keyed on the role, not menu.module.css's
 * hashed class, so it covers the Base UI item (data-disabled) and the plain one (aria-disabled). */
[role='menuitem']:is([data-disabled], [aria-disabled='true']) .itemTextPrimary,
.menuItem:is([data-disabled], :disabled, [aria-disabled='true']) .itemTextPrimary {
  color: var(--tuna-palette-action-disabled);
}

/* ---- Disclosure (inline accordion within a List) ---- */

/* The <li> holding the trigger and its nested list. */
.disclosure {
  list-style: none;
}

.disclosureTrigger {
  justify-content: space-between;
}

/* Rotating chevron driven purely by aria-expanded so SSR and reduced-motion
   render the correct glyph orientation without JS state in CSS. */
.disclosureTrigger::after {
  content: '';
  flex-shrink: 0;
  width: 8px;
  height: 8px;
  margin-left: 8px;
  border-right: 2px solid var(--tuna-palette-action-active);
  border-bottom: 2px solid var(--tuna-palette-action-active);
  transform: rotate(45deg);
  transform-origin: center;
  transition: transform 150ms ease;
}

.disclosureTrigger[aria-expanded='false']::after {
  transform: rotate(-45deg);
}

.disclosureContent {
  list-style: none;
  margin: 0;
  padding: 0;
  padding-inline-start: var(--tuna-spacing-3);
  overflow: hidden;
  block-size: auto;
  /* `visibility` rides along discretely (allow-discrete): it flips at the END
   * of the collapse animation and the START of expansion, so the height
   * animation stays smooth while collapsed content is fully removed from the
   * accessibility tree and the focus order (WCAG 2.4.3 / 4.1.2 — a visually
   * hidden menu item must not be SR-readable or Tab-reachable). Browsers
   * without allow-discrete hide instantly on collapse, which is safe. */
  transition:
    block-size 150ms ease,
    visibility 150ms allow-discrete;
}

/* Lets block-size animate to/from `auto` in supporting browsers. */
@supports (interpolate-size: allow-keywords) {
  .disclosureContent {
    interpolate-size: allow-keywords;
  }
}

.disclosureContentCollapsed {
  block-size: 0;
  visibility: hidden;
}

@media (prefers-reduced-motion: reduce) {
  .disclosureTrigger::after {
    transition: none;
  }

  .disclosureContent {
    transition: none;
  }
}
